import { FormEvent, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { BRANCH_CODES, BRANCH_LABELS, type BranchCode } from '@lorsamthai/ops-shared';
import { createTransferOrder } from '../lib/queries.js';

export default function TransferNew() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();

  const fromParam = sp.get('from') as BranchCode | null;
  const toParam = sp.get('to') as BranchCode | null;
  const skuParam = sp.get('sku') ?? '';
  const qtyParam = Number(sp.get('qty') ?? '4');
  const isValidBranch = (b: string | null): b is BranchCode =>
    !!b && BRANCH_CODES.includes(b as BranchCode);

  const [fromBranch, setFromBranch] = useState<BranchCode>(
    isValidBranch(fromParam) ? fromParam : 'bp'
  );
  const [toBranch, setToBranch] = useState<BranchCode>(
    isValidBranch(toParam) && toParam !== (isValidBranch(fromParam) ? fromParam : 'bp')
      ? toParam
      : 'ww'
  );
  const [note, setNote] = useState('');
  const [lines, setLines] = useState([
    { sku: skuParam, qty: Number.isFinite(qtyParam) && qtyParam > 0 ? qtyParam : 4 },
  ]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const items = lines.filter((l) => l.sku.trim() && l.qty > 0);
    if (items.length === 0) return setError('ต้องมีอย่างน้อย 1 รายการ');
    if (fromBranch === toBranch) return setError('สาขาต้นทางและปลายทางต้องต่างกัน');

    setSubmitting(true);
    try {
      const tr = await createTransferOrder({
        from_branch: fromBranch,
        to_branch: toBranch,
        items: items.map((l) => ({ sku: l.sku.trim(), qty: l.qty })),
        note: note || undefined,
        mark_approved: true,
      });
      navigate(`/transfers?highlight=${tr.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <Link to="/transfers" className="text-xs text-slate-500 hover:underline">
          ← กลับรายการ
        </Link>
        <h1 className="mt-1 text-xl font-bold">สร้างใบโอน</h1>
      </div>

      <form onSubmit={submit} className="space-y-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="space-y-1 text-sm">
              <span className="block font-medium">จากสาขา</span>
              <select
                value={fromBranch}
                onChange={(e) => setFromBranch(e.target.value as BranchCode)}
                className="w-full rounded-md border border-slate-200 bg-white px-3 py-1.5"
              >
                {BRANCH_CODES.map((b) => <option key={b} value={b}>{BRANCH_LABELS[b]}</option>)}
              </select>
            </label>
            <label className="space-y-1 text-sm">
              <span className="block font-medium">ไปสาขา</span>
              <select
                value={toBranch}
                onChange={(e) => setToBranch(e.target.value as BranchCode)}
                className="w-full rounded-md border border-slate-200 bg-white px-3 py-1.5"
              >
                {BRANCH_CODES.filter((b) => b !== fromBranch).map((b) => (
                  <option key={b} value={b}>{BRANCH_LABELS[b]}</option>
                ))}
              </select>
            </label>
            <label className="sm:col-span-2 space-y-1 text-sm">
              <span className="block font-medium">หมายเหตุ</span>
              <input value={note} onChange={(e) => setNote(e.target.value)}
                className="w-full rounded-md border border-slate-200 px-3 py-1.5" />
            </label>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold">รายการ SKU</h2>
            <button type="button" onClick={() => setLines((p) => [...p, { sku: '', qty: 1 }])}
              className="rounded-md border border-slate-200 px-2 py-1 text-xs hover:bg-slate-50">
              + เพิ่ม
            </button>
          </div>
          <div className="mt-3 space-y-2">
            {lines.map((l, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  value={l.sku}
                  onChange={(e) => setLines((p) => p.map((x, j) => j === i ? { ...x, sku: e.target.value } : x))}
                  placeholder="SKU"
                  className="flex-1 rounded-md border border-slate-200 px-2 py-1 font-mono text-xs"
                />
                <input type="number" min={1}
                  value={l.qty}
                  onChange={(e) => setLines((p) => p.map((x, j) => j === i ? { ...x, qty: Number(e.target.value) } : x))}
                  className="w-20 rounded-md border border-slate-200 px-2 py-1 text-right"
                />
                <button type="button" onClick={() => setLines((p) => p.filter((_, j) => j !== i))}
                  disabled={lines.length === 1}
                  className="text-xs text-slate-400 hover:text-red-500">
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>

        {error && <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>}

        <div className="flex justify-end">
          <button type="submit" disabled={submitting}
            className="rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
            {submitting ? 'กำลังบันทึก...' : 'บันทึกใบโอน'}
          </button>
        </div>
      </form>
    </div>
  );
}
